// ============================================================================
// PropertyAccessGuard: capability-based — Owner (owns property) or Team Admin (has access).
// Cleaner-only users are redirected to /worker/schedule.
// ============================================================================

import { ReactNode, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PropertyAccessGuardProps {
  children: ReactNode;
}

export default function PropertyAccessGuard({ children }: PropertyAccessGuardProps) {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { user, profile, loading } = useAuth();
  const { is_owner, is_cohost, is_cleaner, isLoading: capsLoading } = useUserCapabilities();

  const canAttemptAccess = is_owner || is_cohost;

  const { data: accessRow, isLoading: accessLoading } = useQuery({
    queryKey: ["property-access-guard", propertyId, user?.id],
    queryFn: async () => {
      if (!propertyId || !user?.id) return null;
      const { data, error } = await supabase.rpc("get_my_property_access_level", {
        p_property_id: propertyId,
      });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      return row as { access_level: string; is_owner: boolean } | null;
    },
    enabled: !!propertyId && !!user?.id && canAttemptAccess,
  });

  const hasAccess = !!accessRow;

  useEffect(() => {
    if (loading || capsLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!canAttemptAccess && is_cleaner) {
      navigate("/worker/schedule", { replace: true });
      return;
    }
    if (!canAttemptAccess) {
      navigate("/", { replace: true });
      return;
    }
    if (!accessLoading && !hasAccess && propertyId) {
      navigate("/owner/properties", { replace: true });
    }
  }, [loading, capsLoading, user, accessLoading, hasAccess, propertyId, canAttemptAccess, is_owner, is_cleaner, navigate]);

  if (loading || capsLoading || (canAttemptAccess && accessLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user || !canAttemptAccess) return null;
  if (!hasAccess && propertyId) return null;

  return <>{children}</>;
}
